import {useEffect, useMemo, useState} from "react";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {BackButton, ProfileCard} from "@/components";
import {trpc} from "@/app/trpc";
import {useAuth, useLogout} from "@/hooks/useAuth";
import {showErrorToast, showSuccessToast} from "@/utils/toast";
import {Field} from "@headlessui/react";
import {
  Button,
  CarNumberInput,
  DropdownSelect,
  Input,
  Label,
  PhoneInput,
  ShadowBox,
  YStack,
  StickyHeader,
} from "@chayka/ui";
import {USER_TYPE_ITEMS} from "@/constants/userTypes";

export const ProfilePage = () => {
  const queryClient = useQueryClient();
  const {user} = useAuth();
  const logoutMutator = useLogout();
  const [type, setType] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [carNumber, setCarNumber] = useState("");
  const [carModel, setCarModel] = useState("");

  useEffect(() => {
    setType(user?.userType ?? "");
    setFirstName(user?.firstName ?? "");
    setLastName(user?.lastName ?? "");
    setCarNumber(user?.carNumber ?? "");
    setCarModel(user?.carModel ?? "");
  }, [user]);

  const updateMeMutator = useMutation({
    ...trpc.auth.updateMe.mutationOptions(),
    onSuccess: async () => {
      await queryClient.invalidateQueries(trpc.auth.me.queryFilter());
      showSuccessToast("Профиль успешно обновлён");
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const selectedUserType = useMemo(
    () => USER_TYPE_ITEMS.find((item) => item.value === type) ?? null,
    [type],
  );

  const isDirty =
    type !== (user?.userType ?? "") ||
    firstName !== (user?.firstName ?? "") ||
    lastName !== (user?.lastName ?? "") ||
    carNumber !== (user?.carNumber ?? "") ||
    carModel !== (user?.carModel ?? "");

  const handleSave = () => {
    updateMeMutator.mutate({
      type: type.trim() || undefined,
      firstName: firstName.trim() || undefined,
      lastName: lastName.trim() || undefined,
      carNumber: carNumber.trim() || undefined,
      carModel: carModel.trim() || undefined,
    });
  };

  const handleLogout = () => {
    logoutMutator.mutate(undefined, {
      onSuccess: () => {
        showSuccessToast("Вы вышли из аккаунта");
      },
      onError: (error) => {
        showErrorToast(error.message);
      },
    });
  };

  return (
    <>
      <StickyHeader title="Профиль" leftPart={<BackButton />} />
      <ShadowBox className="rounded-xl p-6">
        <ProfileCard />
      </ShadowBox>

      <Field>
        <Label>Логин</Label>
        <Input value={user?.login} readOnly disabled />
      </Field>

      <Field className="dropdown z-1">
        <Label>Тип пользователя</Label>
        <DropdownSelect
          placeholder="Не выбрано"
          selected={selectedUserType}
          items={USER_TYPE_ITEMS}
          onSelect={(item) => setType(item.value)}
        />
      </Field>

      <Field>
        <Label>Данные</Label>
        <YStack className="gap-4">
          <Input
            value={firstName}
            placeholder="Имя"
            onChange={(e) => setFirstName(e.target.value)}
            autoComplete="given-name"
          />
          <Input
            value={lastName}
            placeholder="Фамилия"
            onChange={(e) => setLastName(e.target.value)}
            autoComplete="family-name"
          />
          <PhoneInput value={user?.phone ?? ""} readOnly disabled />
          <Input
            value={user?.email ?? ""}
            placeholder="Электронная почта"
            readOnly
            disabled
            autoComplete="email"
          />
        </YStack>
      </Field>

      <Field>
        <Label>Транспортные данные</Label>
        <YStack className="gap-4">
          <CarNumberInput value={carNumber} onChange={(e) => setCarNumber(e.target.value)} />
          <Input
            value={carModel}
            placeholder="Марка машины"
            onChange={(e) => setCarModel(e.target.value)}
          />
        </YStack>
      </Field>

      <Button
        variant="primary"
        className="w-full"
        onClick={handleSave}
        disabled={!user || !isDirty}
        loading={updateMeMutator.isPending || logoutMutator.isPending}
      >
        Сохранить
      </Button>
      <Button
        variant="ghost"
        className="w-full"
        onClick={handleLogout}
        loading={logoutMutator.isPending}
      >
        Выйти из аккаунта
      </Button>
    </>
  );
};
